<template>
  <section class="catalog-products" style="width: 100%; height: 100%">
    <div
      class="modal-card"
      style="width: 100%; height: 100%; max-height: none; padding: 0"
    >
      <header class="modal-card-head">
        <p class="modal-card-title">
          {{ $t('attributes.catalog_products') }} ({{ count }} / {{ total }})
        </p>
      </header>
      <section class="modal-card-body">
        <nav class="columns">
          <div class="column">
            <b-autocomplete
              ref="product_code_input"
              v-model="search"
              name="search"
              field="code"
              :custom-formatter="p => `${p.code} - ${p.name}`"
              :data="products"
              :loading="isLoadingProducts"
              :placeholder="$t('message.please_input_product_code')"
              :open-on-focus="false"
              keep-first
              @typing="loadProducts"
              @select="onSelectProduct"
            >
              <template #default="props">
                <p>
                  {{ props.option.code }} -
                  <!-- eslint-disable-next-line-->
                  <span v-html="props.option.name"></span>
                </p>
              </template>
              <template #empty>
                {{
                  search
                    ? isLoadingProducts
                      ? $t('attributes.searching')
                      : $t('attributes.not_found')
                    : $t('message.please_input_product_code')
                }}
              </template>
            </b-autocomplete>
          </div>
          <div class="column is-narrow">
            <b-button
              :loading="sending || loading || isLoadingProducts"
              type="is-primary"
              @click.prevent.stop="onAdd"
              :disabled="!selectedProductCode"
            >
              {{ $t('attributes.catalog_add_product') }}
            </b-button>
          </div>
        </nav>
        <b-table
          :data="items"
          :loading="loading"
          :mobile-cards="false"
          bordered
          draggable
          @dragstart="dragstart"
          @drop="drop"
          @dragover="dragover"
          @dragleave="dragleave"
        >
          <b-table-column :width="80" label="功能" centered v-slot="props">
            <div class="buttons is-centered">
              <b-button
                type="is-danger"
                size="is-small"
                outlined
                @click.prevent.stop="onRemove(props.row)"
              >
                {{ $t('attributes.remove') }}
              </b-button>
            </div>
          </b-table-column>
          <b-table-column
            field="sort"
            :label="$t('attributes.sort_drag')"
            numeric
            centered
            width="100"
            v-slot="props"
          >
            {{ props.row.sort }}
          </b-table-column>
          <b-table-column
            field="product_code"
            :label="$t('attributes.product_code')"
            width="100"
            centered
            v-slot="props"
          >
            <a
              :href="`/products?status=all&code=${props.row.product_code}`"
              target="_blank"
            >
              {{ props.row.product_code }}
            </a>
          </b-table-column>
          <b-table-column
            field="shelve"
            :label="$t('attributes.activation_status')"
            width="100"
            centered
            v-slot="props"
          >
            <b-tag
              :type="
                props.row.is_deprecated
                  ? 'is-warning'
                  : props.row.product.shelve_on
                  ? 'is-info'
                  : 'is-danger'
              "
              size="is-medium"
            >
              {{ groupShelveStatus(props.row.product) }}
            </b-tag>
          </b-table-column>
          <b-table-column
            field="product_name"
            :label="$t('attributes.product_name')"
            v-slot="props"
          >
            <p v-html="props.row.product.name"></p>
          </b-table-column>

          <template #empty>
            <section class="section">
              <div
                v-if="!loading"
                class="content has-text-grey has-text-centered"
              >
                <p>
                  <b-icon icon="emoticon-sad" size="is-large" />
                </p>
              </div>
            </section>
          </template>
        </b-table>
      </section>
      <footer class="modal-card-foot">
        <div class="buttons is-right" style="width: 100%;">
          <button class="button" type="button" @click="$parent.close()">
            {{ $t('attributes.close') }}
          </button>
          <b-button
            :loading="sending || loading"
            type="is-primary"
            @click="updateSort"
          >
            {{ $t('attributes.update') + $t('attributes.sort') }}
          </b-button>
        </div>
      </footer>
    </div>
  </section>
</template>

<script>
import { CATALOG_API, PRODUCTS_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import debounce from 'lodash/debounce'

export default {
  name: 'CatalogProductsFrom',

  props: {
    areaCode: {
      required: true,
      type: String
    },
    catalogNo: {
      required: true,
      type: String
    }
  },
  data() {
    return {
      loading: false,
      sending: false,

      isLoadingProducts: false,
      products: [],
      search: null,
      selectedProductCode: null,
      selectedProductName: null,

      items: [],
      off_items: [],

      count: 0,
      total: 0,

      sortField: 'sort',
      sortOrder: 'asc',
      defaultSortOrder: 'asc',

      draggingRow: null,
      draggingRowIndex: null
    }
  },
  watch: {
    catalogNo(number) {
      this.loadItems(number)
    }
  },
  mounted() {
    this.loadItems(this.catalogNo)
  },
  methods: {
    async loadItems(catalogNo) {
      try {
        const api = `${CATALOG_API}/${catalogNo}/products`

        this.loading = true
        const res = await this.axios.get(api)
        this.total = res.data.data.length
        this.items = res.data.data.filter(p => p.product.shelve_on)
        this.off_items = res.data.data.filter(p => !p.product.shelve_on)
        this.count = this.items.length
      } catch (e) {
        this.items = []
        this.total = 0
        this.off_items = []
        this.count = 0

        notifyService.fetch_error('products', e)
      } finally {
        this.loading = false
      }
    },
    async loadAsyncProducts() {
      try {
        const api = `${PRODUCTS_API}/list`

        const params = {
          area: this.areaCode,
          code: this.search
        }

        this.isLoadingProducts = true
        const res = await this.axios.get(api, { params })

        const codes = this.items.map(p => p.product_code)
        codes.push(...this.off_items.map(p => p.product_code))
        this.products = res.data.data.filter(p => !codes.includes(p.code))
      } catch (e) {
        this.products = []

        notifyService.fetch_error('product', e)
      } finally {
        this.isLoadingProducts = false
      }
    },
    loadProducts: debounce(function() {
      this.loadAsyncProducts()
    }, 500),
    onSelectProduct(option) {
      this.selectedProductCode = option && option.code
      this.selectedProductName = option && option.name
    },
    dragstart(payload) {
      this.draggingRow = payload.row
      this.draggingRowIndex = payload.index
      payload.event.dataTransfer.effectAllowed = 'copy'
    },
    dragover(payload) {
      payload.event.dataTransfer.dropEffect = 'copy'
      payload.event.target.closest('tr').classList.add('is-selected')
      payload.event.preventDefault()
    },
    dragleave(payload) {
      payload.event.target.closest('tr').classList.remove('is-selected')
      payload.event.preventDefault()
    },
    async drop(payload) {
      payload.event.target.closest('tr').classList.remove('is-selected')
      const droppedOnRowIndex = payload.index

      let items = [...this.items]

      items.splice(this.draggingRowIndex, 1)

      items.splice(droppedOnRowIndex, 0, this.draggingRow)

      this.items = items.map((item, idx) => {
        return { ...item, sort: idx + 1 }
      })
    },
    async onAdd() {
      try {
        this.sending = true
        const api = `${CATALOG_API}/${this.catalogNo}/products`
        const result = await this.axios.post(api, {
          product_code: this.selectedProductCode
        })
        this.items.push(result.data.data)
        this.count = this.items.length

        this.search = null
        this.selectedProductCode = null
        this.selectedProductName = null
        notifyService.success(this.$t('attributes.add_succeed'))
      } catch (e) {
        notifyService.create_error('catalog_product', e)
      } finally {
        this.sending = false
      }
    },
    async onRemove(item) {
      try {
        this.sending = true
        const api = `${CATALOG_API}/${this.catalogNo}/products`
        await this.axios.delete(api, {
          data: { product_code: item.product_code }
        })

        this.items = this.items.filter(
          p => p.product_code !== item.product_code
        )
        this.count = this.items.length

        notifyService.success(this.$t('attributes.remove_succeed'))
      } catch (e) {
        notifyService.delete_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateSort() {
      try {
        const items = this.items.map(item => ({
          product_code: item.product_code,
          sort: item.sort
        }))
        const off_items = this.off_items.map(item => ({
          product_code: item.product_code,
          sort: item.sort
        }))

        items.push(...off_items)

        this.sending = true
        const api = `${CATALOG_API}/${this.catalogNo}/products-sort`
        await this.axios.put(api, {
          items: items
        })

        notifyService.success(this.$t('attributes.update_succeed'))
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    }
  }
}
</script>
